/**
 * Theme preference resolver
 * Manages light/dark/auto theme selection with localStorage persistence
 */

const STORAGE_KEY = 'theme-preference';
const THEME_ATTR = 'data-theme';

class ThemeManager {
  constructor() {
    this.mediaQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    this.preference = this.getStoredPreference() || 'auto';
    this.systemPreference = this.getSystemPreference();

    this.init();
  }

  init() {
    // Apply initial theme
    this.applyTheme();
    
    // Listen for system preference changes
    if (this.mediaQuery) {
      this.mediaQuery.addEventListener('change', (e) => {
        this.systemPreference = e.matches ? 'dark' : 'light';
        if (this.preference === 'auto') {
          this.applyTheme();
        }
      });
    }
    
    // Dispatch ready event
    document.dispatchEvent(new CustomEvent('theme:ready', {
      detail: { preference: this.preference, resolved: this.getResolvedTheme() }
    }));
  }

  getSystemPreference() {
    if (this.mediaQuery) {
      return this.mediaQuery.matches ? 'dark' : 'light';
    }
    return 'light';
  }

  getStoredPreference() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  getResolvedTheme() {
    return this.preference === 'auto' ? this.systemPreference : this.preference;
  }

  setPreference(value) {
    if (!['light', 'dark', 'auto'].includes(value)) {
      console.warn(`Invalid theme preference: ${value}`);
      return;
    }

    this.preference = value;
    
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      console.warn('Failed to persist theme preference:', e);
    }

    this.applyTheme();
    
    document.dispatchEvent(new CustomEvent('theme:change', {
      detail: { preference: value, resolved: this.getResolvedTheme() }
    }));
  }

  applyTheme() {
    const resolved = this.getResolvedTheme();
    document.documentElement.setAttribute(THEME_ATTR, resolved);
  }

  getPreference() {
    return this.preference;
  }
}

// Initialize and expose globally
const themeManager = new ThemeManager();
window.themeManager = themeManager;

export default themeManager;
